/**
 * 8-bit sprite definitions for Message Invaders.
 *
 * Each sprite is an array of strings. Every character maps to a color
 * via PALETTE. '.' is transparent.
 *
 * Sprites are 12×12 unless noted otherwise.
 */

export const PALETTE: Record<string, string> = {
  '.': 'transparent',
  W: '#ffffff',
  R: '#ff4457',
  G: '#44dd66',
  B: '#4499ff',
  Y: '#ffdd44',
  P: '#bb55ff',
  O: '#ff8844',
  C: '#44ddee',
  D: '#666677',
  K: '#1a1a2e',
  L: '#66ffaa',
  M: '#ff55aa',
  S: '#99aabb',
  T: '#ff8888',
  U: '#7744ff',
  H: '#2a2a4a',
  E: '#33cc77',
  F: '#ff6633',
  V: '#aa88ff',
  X: '#ffaa00',
  Z: '#88ccff',
};

// ── Player: sleek upward-pointing ship / paper plane ──────────────
export const PLAYER: string[] = [
  '......CC......',
  '.....CCCC.....',
  '.....CCCC.....',
  '....CCCCCC....',
  '....CCCCCC....',
  '...CCWWWCCC...',
  '..CCCWWWCCCC..',
  '.CCCCWWWCCCCC.',
  'CCCCCWWWCCCCCC',
  'CC.CCCCCCCC.CC',
  'C...CCCCCC...C',
  '....CC..CC....',
];

// ── Churn Monster: big menacing creature, red tones ───────────────
export const CHURN_MONSTER: string[] = [
  '..RRRRRRRR..',
  '.RRTRRRRTRR.',
  'RRRRRRRRRRRR',
  'RRR.RRRR.RRR',
  'RRRRRRRRRRRR',
  'RRRRTTTTRRR.',
  '.RRRRRRRRRR.',
  '..RRRRRRRR..',
  '..RR.RR.RR..',
  '.RR..RR..RR.',
  'RR..RRRR..RR',
  'R....RR....R',
];

// ── Unsubscribe Gremlin: small trickster, green ────────────────────
export const UNSUB_GREMLIN: string[] = [
  'G...........G',
  'GG....GG...GG',
  '.GG..GGGG.GG.',
  '..GGGGGGGGGG.',
  '..GGG.GG.GGG.',
  '..GGGGGGGGGG.',
  '...GGGKKGGG..',
  '...GGGGGGGG..',
  '....GGGGGG...',
  '...GG....GG..',
  '..GG......GG.',
  '.GG........GG',
];

// ── Bounce Demon: winged purple menace ─────────────────────────────
export const BOUNCE_DEMON: string[] = [
  'P..........P',
  'PP..PPPP..PP',
  'PPP.PPPP.PPP',
  'PPPPPPPPPPPP',
  'PPP.P..P.PPP',
  'PPPPPPPPPPPP',
  '.PPPPPPPPPP.',
  '..PPPMMPPP..',
  '..PPPPPPPP..',
  '...PP..PP...',
  '..PP....PP..',
  '.PP......PP.',
];

// ── Spam Trap Spider: creepy 8-legged thing, orange/dark ───────────
export const SPAM_SPIDER: string[] = [
  'O..O....O..O',
  '.OO.OOOO.OO.',
  '..OOOOOOOO..',
  '.OOOO..OOOO.',
  '.OOO.OO.OOO.',
  'OOOOOOOOOOOO',
  'O.OOOFFOOO.O',
  '..OOOOOOOO..',
  '.OO.OOOO.OO.',
  'O.O..OO..O.O',
  'O..O....O..O',
  '...O....O...',
];

// ── Projectiles ────────────────────────────────────────────────────

// Email bullet (6×8) — tiny envelope going up
export const BULLET_EMAIL: string[] = ['CWWWWC', 'CCWWCC', 'CWCCWC', 'CWCCWC', 'CWCCWC', 'CWWWWC', 'CCCCCC'];

// Push notification bullet (6×8) — faster, cyan
export const BULLET_PUSH: string[] = ['..CC..', '.CCCC.', 'CCCCCC', 'CCZZCC', 'CCZZCC', 'CCCCCC', '.CCCC.', '..CC..'];

// In-app message bullet (8×6) — wider
export const BULLET_INAPP: string[] = ['LLLLLLLL', 'LGGGGGGL', 'LGGGGGGL', 'LGGGGGGL', 'LLLLLLLL', '...LL...'];

// Enemy projectile — red bolt dropping down (4×8)
export const ENEMY_BOLT: string[] = ['.RR.', '.RR.', 'RRRR', '.RR.', '.RR.', 'RRRR', '.RR.', '.RR.'];

// ── Power-ups (10×10) ──────────────────────────────────────────────

// Broadcast — megaphone, yellow
export const POWERUP_BROADCAST: string[] = [
  '....YYYY..',
  '...YYYYY..',
  '..YYYYYY..',
  'YYYYYYYYYY',
  'YYYXXYYYY.',
  'YYYXXYYYY.',
  'YYYYYYYYYY',
  '..YYYYYY..',
  '...YYYYY..',
  '....YYYY..',
];

// Segment — grid icon, cyan
export const POWERUP_SEGMENT: string[] = [
  'CCCCCCCCCC',
  'C.CC.CC.CC',
  'C.CC.CC.CC',
  'CCCCCCCCCC',
  'C.CC.CC.CC',
  'C.CC.CC.CC',
  'CCCCCCCCCC',
  'C.CC.CC.CC',
  'C.CC.CC.CC',
  'CCCCCCCCCC',
];

// A/B Test — split arrows, green
export const POWERUP_ABTEST: string[] = [
  '..GG..GG..',
  '.GGGG.GGG.',
  'GGGGGGGGG.',
  '.GGGG.GGG.',
  '..GG...GG.',
  '..GG...GG.',
  '.GGG..GGGG',
  '.GGGGGGGGG',
  '.GGG..GGGG',
  '..GG...GG.',
];

// Shield — deliverability shield, blue
export const POWERUP_SHIELD: string[] = [
  '..BBBBBB..',
  '.BBBBBBBB.',
  'BBBBBBBBBB',
  'BBBBWWBBBB',
  'BBBWWWWBBB',
  'BBBBWWBBBB',
  'BBBBBBBBBB',
  '.BBBBBBBB.',
  '..BBBBBB..',
  '...BBBB...',
];

// ── Explosion frames (12×12) ──────────────────────────────────────

export const EXPLOSION_1: string[] = [
  '............',
  '............',
  '....YYYY....',
  '...YYYYYY...',
  '..YYYYYYYY..',
  '..YYYYYYYY..',
  '..YYYYYYYY..',
  '..YYYYYYYY..',
  '...YYYYYY...',
  '....YYYY....',
  '............',
  '............',
];

export const EXPLOSION_2: string[] = [
  '..Y......Y..',
  '...YY..YY...',
  'Y...YYYY...Y',
  '.YY.YYYY.YY.',
  '..YYYYYYYY..',
  '..YYYYYYYY..',
  '..YYYYYYYY..',
  '.YY.YYYY.YY.',
  'Y...YYYY...Y',
  '...YY..YY...',
  '..Y......Y..',
  '............',
];

export const EXPLOSION_3: string[] = [
  '.R..Y..Y..R.',
  '..RR.YY.RR..',
  '.R..OOOO..R.',
  '..ROOOOOR...',
  'Y.OOOOOOOO.Y',
  'Y.OOOOOOOO.Y',
  '..ROOOOOR...',
  '.R..OOOO..R.',
  '..RR.YY.RR..',
  '.R..Y..Y..R.',
  '............',
  '............',
];

/**
 * Draw a sprite on a canvas context at the given position.
 * `scale` controls pixel size (e.g. 2 = each sprite pixel is 2×2 canvas pixels).
 */
export function drawSprite(
  context: CanvasRenderingContext2D,
  sprite: string[],
  x: number,
  y: number,
  scale: number,
  flash = false,
) {
  for (let row = 0; row < sprite.length; row++) {
    for (let col = 0; col < sprite[row].length; col++) {
      const ch = sprite[row][col];
      if (ch === '.') continue;
      const color = flash ? '#ffffff' : PALETTE[ch] ?? '#ff00ff';
      context.fillStyle = color;
      context.fillRect(x + col * scale, y + row * scale, scale, scale);
    }
  }
}

/** Get sprite dimensions in canvas pixels. */
export function spriteSize(sprite: string[], scale: number): { w: number; h: number } {
  const h = sprite.length * scale;
  const w = (sprite[0]?.length ?? 0) * scale;
  return { w, h };
}
